import React from 'react';
import { 
    Box, 
    Typography, 
    Grid, 
    Paper,
    Divider,
    Chip
} from '@mui/material';
import { SandboxConsentDetails } from '../../../services/sandbox-consents-service';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

interface CollectionPointTabProps {
    consentData: SandboxConsentDetails;
}

const CollectionPointTab: React.FC<CollectionPointTabProps> = ({ consentData }) => {
    const { collectionPoint } = consentData;

    return (
        <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Collection Point Details
            </Typography>

            <Grid container spacing={3}>
                {/* Basic Collection Point Information */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                            Basic Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Collection Point ID</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    #{collectionPoint.id}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Name</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {collectionPoint.name}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Type</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {collectionPoint.type}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Identifier</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium', fontFamily: 'monospace' }}>
                                    {collectionPoint.identifier}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Department and Description */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                            Organization & Description
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Department</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {collectionPoint.departmentName}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Description</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {collectionPoint.description}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Identifier Data Element */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                            Identifier Data Element
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Element Name</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {collectionPoint.identifierDataElement?.name || 'Not specified'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Element Type</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {collectionPoint.identifierDataElement?.type || 'Not specified'}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Features and Settings */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                            Features & Settings
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary">Geolocation Tracking</Typography>
                                {collectionPoint.trackGeolocation ? (
                                    <Chip 
                                        icon={<LocationOnIcon />} 
                                        label="Enabled" 
                                        color="success" 
                                        size="small" 
                                        variant="outlined"
                                    />
                                ) : (
                                    <Chip 
                                        icon={<LocationOffIcon />} 
                                        label="Disabled" 
                                        color="default" 
                                        size="small" 
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary">Minor Handling</Typography>
                                {collectionPoint.minorEnabled ? (
                                    <Chip 
                                        icon={<CheckCircleIcon />} 
                                        label="Enabled" 
                                        color="success" 
                                        size="small" 
                                        variant="outlined"
                                    />
                                ) : (
                                    <Chip 
                                        icon={<CancelIcon />} 
                                        label="Disabled" 
                                        color="default" 
                                        size="small" 
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Parent Identifier (if minor handling is enabled) */}
                {collectionPoint.minorEnabled && collectionPoint.parentIdentifier && (
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                                Parent Identifier Configuration
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Parent Element Name</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                            {collectionPoint.parentIdentifier.name}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Parent Element Type</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                            {collectionPoint.parentIdentifier.type}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default CollectionPointTab;
